/* eslint-disable @typescript-eslint/no-explicit-any */
import { type App, getCurrentInstance } from 'vue'
import Rollbar from 'rollbar/replay'
import type { CurrentUser } from '../data/user/types'
import { getConfig } from '../conf'
import { isConnectionError } from '/@/utils/http'

export function useRollbar() {
  const vm = getCurrentInstance()
  if (!vm) {
    // eslint-disable-next-line no-console
    console.error(
      'Do not call `useRollbar()` outside of a component `setup()` lifecycle.',
    )
    return {
      error() {
        // noop
      },
      info() {
        // noop
      },
      configure() {
        // noop
      },
    }
  }

  return {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    error(e: string | Error, ...extra: any[]) {
      vm?.appContext.app.$rollbar.error(e, ...extra)
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    info(e: string | Error, ...extra: any[]) {
      vm?.appContext.app.$rollbar.info(e, ...extra)
    },
    configure(user: CurrentUser) {
      vm?.appContext.app.$rollbar.configure({
        payload: {
          person: {
            id: user.id,
            username: `${user.firstName} ${user.lastName}`,
            email: user.email,
          },
        },
      })
    },
  }
}

// Payload defined here: https://docs.rollbar.com/reference/create-item
function extractError(payload: any) {
  if (payload.body.trace) {
    return payload.body.trace.exception.message
  }
  if (payload.body.trace_chain?.length) {
    return payload.body.trace_chain[0].exception.message
  }
  if (payload.body.message) {
    return payload.body.message.body
  }
  return null
}
function extractTraceSources(payload: any) {
  if (payload.body.trace) {
    return (payload.body.trace.frames as any[]).map(f => f.filename)
  }
  if (payload.body.trace_chain?.length) {
    return (payload.body.trace_chain[0].frames as any[]).map(f => f.filename)
  }
  return []
}

const IGNORED_ERRORS = [
  // Thrown by graphql when login tokens don't match. We display a messsage to the user
  // via a webhook on the apollo client so the logged message can be ignored.
  'Received status code 409',
  // we can safely ignore all `"Navigation aborted ..."` errors, as they are
  // produced by a call to `next(false)` inside navigation guards (e.g.
  // onBeforeRouteLeave), in case the navigation is triggered by a call to `router.
  // push()`.
  // i.e. in all cases, we meant for the navigation to be aborted, so there's
  // nothing to be warned about.
  'Navigation aborted',
  // we can ignore this errors as they happen regularly when a page is forcefully
  // refreshed while it's still loading, sending a lot of unnecessary logs
  'Importing a module script failed',
  // Vite's module preload polyfill fires this when a CSS chunk fails to load
  // due to network issues. Unactionable client-side connectivity error.
  'Unable to preload CSS for',
  // We don't care about errors from 3rd-party chrome extensions
  'chrome-extension',
  'Extension context invalidated',
  // Rollbar's own internal message when it can't read the `reason` from an
  // unhandled rejection event (e.g. cross-origin or opaque rejections).
  // There is no actionable information in these occurrences.
  '[unhandledrejection] error getting `reason` from event',
]

function isIgnoredError(errorMessage: string | null) {
  return IGNORED_ERRORS.some(ignored => errorMessage?.includes(ignored))
}

const config = getConfig()

let onSendHook:
  | ((isUncaught: boolean, errorMessage: string | null) => void)
  | undefined

export function configureOnSend(
  hook: (isUncaught: boolean, errorMessage: string | null) => void,
) {
  onSendHook = hook
}

const rollbarInstance = new Rollbar({
  accessToken: '422f528aec494fda843258929ca24ffe',
  captureUncaught: true,
  captureUnhandledRejections: true,
  enabled: true,
  replay: {
    enabled: !import.meta.env.VITEST && !import.meta.env.DEV,
    // Sample 5% of error occurrences to stay within the 1k replay/month limit
    // (we generate ~20k events/month). Raise this if the plan limit increases.
    triggerDefaults: {
      samplingRatio: 0.05,
    },
    // Elements with this class are replaced with a placeholder in recordings.
    // Stripe payment iframes are cross-origin and are blocked automatically by the recorder.
    blockClass: 'rb-block',
    // Mask sensitive input values
    maskInputOptions: {
      password: true,
      email: true,
      tel: true,
    },
  },
  scrubPaths: ['body.message.extra.e.config.headers.Authorization'],
  payload: {
    environment: config.env,
    context: window.location.href,
    client: {
      javascript: {
        code_version: config.version,
        source_map_enabled: true,
        guess_uncaught_frames: true,
      },
    },
  },
  // @ts-ignore
  checkIgnore(isUncaught, args, payload) {
    const errorMessage = extractError(payload)
    if (isIgnoredError(errorMessage) || isConnectionError(errorMessage)) {
      return true
    }
    const traceSources = extractTraceSources(payload)
    if (traceSources.some(source => source.includes('chrome-extension'))) {
      return true
    }
    return false
  },
  onSendCallback(isUncaught, args, payload) {
    onSendHook?.(isUncaught, extractError(payload))
  },
})

export default {
  install(app: App) {
    if (config.mode === 'development') {
      // Rollbar is messing up with source maps. Mocking it on dev.
      app.use({
        install(app) {
          app.$rollbar = {
            // @ts-ignore
            error(e: unknown, ...extra: unknown[]) {
              // eslint-disable-next-line no-console
              console.error('RollbarMock Error:', e, ...extra)
            },
            info(e: unknown, ...extra: unknown[]) {
              // eslint-disable-next-line no-console
              console.info('RollbarMock Info:', e, ...extra)
            },
            configure(options: Rollbar.Configuration) {
              // eslint-disable-next-line no-console
              console.info('RollbarMock:', 'configure', options)
            },
          }
        },
      })
    } else {
      app.use({
        install(app) {
          app.$rollbar = rollbarInstance
        },
      })
    }
  },
}

export async function reportError(
  error: Error,
  ...extra: Rollbar.LogArgument[]
) {
  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.error('[Rollbar] ', error, ...extra)
    return
  }

  rollbarInstance.error(error, ...extra)
}
